import * as THREE from "three";
import { LevelSim } from "@highmarch/domain";
import { GroundMesh } from "../groundMesh";
import type { PooledVfx } from "../vfxPool";
import { PET_MARKER, PetMarker } from "./petMarker";
import { BURN_COLOUR, DAZZLE_COLOUR, PetFxLook, SHIELD_COLOUR } from "./petFxLook";

/** The lasting statuses' look (world units, seconds; colours 0–1). */
export const PET_STATUS_FX = {
  /** A troop ring (the pup's Rally Bark aura in gold, a Crystal Ward shield in blue), this size at scale 1. */
  auraSize: 0.9,
  /** The raiders' status motes come this often, this high over their feet. */
  every: 0.3, height: 0.55,
  stars: [1, 0.93, 0.45] as [number, number, number],
} as const;

const _p = new THREE.Vector3();

/**
 * What the pet's abilities leave on the field while it lasts (Pets v2/v3, docs/PETS.md), read from the sim each frame,
 * presentation only: a ring under each fortress troop the pet is helping (the pup's Rally Bark: gold, while the bark is
 * on and the troop within its reach; a Crystal Ward shield: blue, while it holds), and over the raiders, every
 * `every` s, a mote of what is on them — dizzy stars while a pet stunned them, embers while the kit's fire burns them,
 * the pet's own glow while it marked them (the owl's lit raiders, the magpie's glint), a twinkle while dazzled, and
 * bubbles round one sealed in the axolotl's bubble.
 */
export class PetStatusFx {
  readonly group = new THREE.Group();
  private readonly rings: PetMarker[] = [];
  private readonly stunned = new Set<number>();
  private moteIn = 0;
  private flareNew = false;
  fx: (() => PooledVfx | null) | null = null;
  troopAt: (id: number) => THREE.Vector3 | null = () => null;
  enemyAt: (id: number) => THREE.Vector3 | null = () => null;

  constructor(private readonly sim: LevelSim, private readonly look: PetFxLook) {}

  /** Raiders a pet just stunned (EnemyStunned with b = 0): their stars run while the stun lasts. */
  stun(ids: readonly number[]): void { for (const id of ids) this.stunned.add(id); }
  /** A pulse went off: the troop rings flare in. */
  flare(): void { for (const r of this.rings) r.flare(); this.flareNew = true; }

  update(dt: number): void {
    this.troopRings(dt);
    if ((this.moteIn -= dt) > 0) return;
    this.moteIn = PET_STATUS_FX.every;
    const fx = this.fx?.();
    if (fx) this.motes(fx);
  }

  /** The rings under the troops the pet is helping now. */
  private troopRings(dt: number): void {
    const sim = this.sim, bark = sim.troops.bark, rule = sim.mods.pet?.bark, r = sim.troops.rally;
    const reach = bark !== null && rule ? rule.reach.toNumber() : -1;
    let n = 0;
    for (const t of sim.troops.all) {
      if (!t.alive || t.ownerTowerId !== 0) continue;
      const dx = t.x.toNumber() - r.x, dy = t.y.toNumber() - r.y, barked = reach >= 0 && dx * dx + dy * dy <= reach * reach, shielded = t.shieldTicks > 0;
      if (!barked && !shielded) continue;
      const at = this.troopAt(t.id);
      if (!at) continue;
      const ring = this.ring(n++, shielded && !barked);
      ring.update(dt, at.x, GroundMesh.drawnAt(at.x, at.z), at.z);
    }
    for (let i = 0; i < this.rings.length; i++) this.rings[i].visible = i < n;
    this.flareNew = false;
  }

  /** The motes over the raiders: stars, embers, the pet's mark, a twinkle, a bubble. */
  private motes(fx: PooledVfx): void {
    const sim = this.sim, L = this.look, h = PET_STATUS_FX.height;
    for (const e of sim.enemySlots) {
      if (!e.alive) { this.stunned.delete(e.id); continue; }
      const stunned = this.stunned.has(e.id) && e.stunTicksLeft > 0;
      if (!stunned) this.stunned.delete(e.id);
      if (!stunned && e.petBurnTicks <= 0 && e.markTicks <= 0 && e.dazzleTicks <= 0) continue;
      const at = this.enemyAt(e.id);
      if (!at) continue;
      _p.set(at.x, at.y + h, at.z);
      if (stunned && L.bubble) fx.bubbles(_p.set(at.x, at.y + h * 0.6, at.z), 3, { color: [...L.signatureColour], radius: 0.28, size: 0.14, life: 0.5 });
      else if (stunned) fx.sparkles(_p, 3, { color: PET_STATUS_FX.stars, radius: 0.18, rise: 0.05, life: 0.4, size: 0.12 });
      if (e.petBurnTicks > 0) fx.embers(_p.set(at.x, at.y + h * 0.5, at.z), 2, { color: [...BURN_COLOUR], radius: 0.15, life: 0.6, size: 0.07 });
      if (e.markTicks > 0) fx.sparkles(_p.set(at.x, at.y + h * 1.25, at.z), 2, { color: [...L.status], radius: 0.12, rise: 0.15, life: 0.45, size: 0.14 });
      if (e.dazzleTicks > 0) fx.sparkles(_p.set(at.x, at.y + h, at.z), 2, { color: [...DAZZLE_COLOUR], radius: 0.22, rise: 0.1, life: 0.35, size: 0.1 });
    }
  }

  private ring(i: number, shield: boolean): PetMarker {
    let r = this.rings[i];
    if (!r) { r = new PetMarker(PET_STATUS_FX.auraSize / PET_MARKER.size); this.rings.push(r); this.group.add(r.mesh); r.flare(); }
    else if (this.flareNew) r.flare();
    r.tint(shield ? SHIELD_COLOUR : PET_MARKER.colour);
    return r;
  }

  dispose(): void { for (const r of this.rings) r.dispose(); this.rings.length = 0; this.group.clear(); }
}
